import React, { useState } from 'react';
import { Button, Form } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import axios from 'axios';
import { useTranslation } from 'react-i18next';

// Same request-shape convention as PasswordResetController on UMS (Phase 6): POST with the
// email in the body — matches how axiosInstance-based calls elsewhere in this codebase are
// structured, but plain `axios` is used here (not axiosInstance) the same way the legacy
// ForgotPass.tsx already calls the gateway directly, since this request needs no session/CSRF.
interface IProps {
  hasLabel?: any;
}

type FormValues = {
  email: string;
};

const validationSchema = Yup.object().shape({
  email: Yup.string().email('Email is invalid').required('Email ID is required'),
});

const ForgotPasswordForm = ({ hasLabel }: IProps) => {
  const { t } = useTranslation();
  const [submitted, setSubmitted] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm<FormValues>({
    resolver: yupResolver(validationSchema),
    mode: 'onChange',
  });

  const onSubmit = handleSubmit(
    async (payload) => {
      try {
        const response = await axios.post(
          `${process.env.REACT_APP_API_GATEWAY_HOST}/api/user-management/v1/users/password/forgot`,
          { email: payload.email }
        );

        toast.success(response.data.responseMsg);
        if (response.data.responseCode === 'SUCCESS') {
          setSubmitted(true);
        }
      } catch (error: any) {
        // Deliberately shown as a toast (unlike legacy ForgotPass.tsx, which silently
        // console.error'd on failure) — NO_MATCHING_ACCOUNT / RATE_LIMITED both have a
        // user-facing message worth surfacing here.
        const message = error?.response?.data?.responseMsg;
        toast.error(message ?? 'Something went wrong. Please try again.');
        console.error('Forgot password error:', error);
      }
    },
    (error: any) => {
      console.error('Handle submit error: ', error);
    }
  );

  if (submitted) {
    return (
      <div className='text-center fs--1'>
        <p>{t('resource_message:forgotPasswordEmailSent')}</p>
        <a href={`${process.env.REACT_APP_CLIENT_APP_HOST}/app`} className='link'>
          {t('resource_message:backToLogin')}
        </a>
      </div>
    );
  }

  return (
    <Form onSubmit={onSubmit}>
      <Form.Group className='mb-3 mt-2'>
        {hasLabel && (
          <Form.Label htmlFor='floatingInput'>{t('resource_2:emailAddress')}</Form.Label>
        )}
        <Form.Control
          placeholder={!hasLabel ? t('resource_2:emailAddress') : ''}
          type='email'
          {...register('email')}
          className={`fs--1 inputBorder form-control ${
            errors.email ? 'is-invalid' : ''
          }`}
          onChange={({ target }) => {
            setValue('email', target.value.trim().toLowerCase());
          }}
        />
        <div className='invalid-feedback'>
          {errors.email?.message?.toString()}
        </div>
      </Form.Group>
      <div className='row'>
        <div className='d-flex justify-content-center align-items-center col-12'>
          <Form.Group className='text-center'>
            <Button type='submit' className='mt-2 w-100'>
              {t('resource_1:submit')}
            </Button>
          </Form.Group>
        </div>
      </div>
    </Form>
  );
};

export default ForgotPasswordForm;

